import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import VipBadge from "../components/VipBadge";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Sparkles, Check, Coins, X } from "lucide-react";

const VIP_PACKS = [
  { key: "7d",   labelKey: "vip.pack_7d",   days: 7,   price: 39 },
  { key: "30d",  labelKey: "vip.pack_30d",  days: 30,  price: 79, popular: true },
  { key: "90d",  labelKey: "vip.pack_90d",  days: 90,  price: 179 },
  { key: "365d", labelKey: "vip.pack_365d", days: 365, price: 499 },
];

const localeMap = { no: "nb-NO", sv: "sv-SE", da: "da-DK", en: "en-GB" };

function VipBuyModal({ open, onClose, balance, onSuccess }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(null);
  if (!open) return null;
  const buy = async (period) => {
    setBusy(period);
    try {
      const r = await api.post("/wallet/buy-vip", { period });
      const pack = VIP_PACKS.find(p=>p.key===period);
      toast.success(t("vip.activated_toast", { label: t(pack.labelKey) }));
      onSuccess?.(r.data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="vip-buy-modal">
      <div className="bg-[var(--noir-elev)] border border-[var(--gold)]/30 max-w-2xl w-full p-8" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="font-meta text-[var(--gold)] mb-1">{t("vip.modal_label")}</div>
            <h2 className="font-fraunces italic text-3xl text-[var(--cream)]">{t("vip.modal_title")}</h2>
          </div>
          <button onClick={onClose} className="text-[var(--cream-soft)] hover:text-[var(--cream)]" data-testid="vip-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex items-center gap-2 mb-6 font-manrope text-sm text-[var(--cream-soft)]">
          <Coins className="h-4 w-4 text-[var(--gold)]" />
          {t("vip.balance")} <strong className="text-[var(--cream)]">{balance} {t("vip.balance_unit")}</strong>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {VIP_PACKS.map((p) => {
            const canAfford = balance >= p.price;
            return (
              <button key={p.key} disabled={!canAfford || busy} onClick={() => buy(p.key)}
                data-testid={`vip-pack-${p.key}`}
                className={`p-5 border text-left transition-all ${
                  canAfford ? "border-white/15 hover:border-[var(--gold)] hover:bg-white/[0.04]" : "border-white/5 opacity-40 cursor-not-allowed"
                } ${p.popular ? "ring-1 ring-[var(--magenta)]/30" : ""}`}>
                {p.popular && <div className="font-meta text-[var(--magenta)] mb-1">{t("vip.most_popular")}</div>}
                <div className="font-fraunces italic text-2xl text-[var(--cream)]">{t(p.labelKey)}</div>
                <div className="font-manrope text-base text-[var(--gold)] inline-flex items-center gap-1.5 mt-2">
                  <Coins className="h-4 w-4" /> {p.price} {t("vip.tokens_short")}
                </div>
                {!canAfford && <div className="font-meta text-red-400 mt-2">{t("vip.need_more", { n: p.price - balance })}</div>}
                {busy === p.key && <div className="font-meta text-[var(--cream-soft)] mt-2">{t("vip.activating")}</div>}
              </button>
            );
          })}
        </div>
        {balance < 39 && (
          <div className="mt-6 border-t border-white/10 pt-5">
            <Link to="/kjop" className="btn-magenta inline-flex items-center gap-2" data-testid="vip-buy-tokens-link">
              <Coins className="h-4 w-4" /> {t("vip.buy_tokens")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Vip() {
  const { t, i18n } = useTranslation();
  const { user, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [showBuy, setShowBuy] = useState(false);

  useEffect(() => { api.get("/vip/info").then(r => setInfo(r.data)).catch(()=>{}); }, []);
  useEffect(() => {
    if (user && user !== false) api.get("/wallet/me").then(r => setWallet(r.data)).catch(()=>{});
  }, [user?.id]);

  if (!info) return <Layout><div className="font-meta text-[var(--cream-soft)]">{t("vip.loading")}</div></Layout>;

  const isVip = info.status?.is_vip;
  const tierUntil = info.status?.tier_until;
  const balance = wallet?.tokens || 0;
  const fmtDate = (iso) => new Date(iso).toLocaleDateString(localeMap[i18n.language] || "nb-NO");

  return (
    <Layout withSidebar={false}>
      <div className="max-w-5xl mx-auto">
        {/* HERO */}
        <div className="border border-[var(--gold)]/30 bg-gradient-to-br from-[var(--gold)]/5 to-transparent p-8 md:p-12 mb-12 relative overflow-hidden" data-testid="vip-hero">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--gold)]/5 rounded-full blur-3xl"></div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <VipBadge user={{tier: "vip"}} size="lg" showTooltip={false} />
              <span className="font-meta text-[var(--gold)]">{t("vip.brand_label")}</span>
            </div>
            <h1 className="font-fraunces italic text-4xl md:text-5xl lg:text-6xl text-[var(--cream)] mb-4 leading-tight">{t("vip.headline")}</h1>
            <p className="font-manrope text-base md:text-lg text-[var(--cream-soft)] leading-relaxed max-w-2xl mb-8">
              {t("vip.lead")}
            </p>

            {isVip ? (
              <div className="flex items-center gap-4 flex-wrap">
                <div className="inline-flex items-center gap-3 px-5 py-3 border border-[var(--gold)] bg-[var(--gold)]/10">
                  <Check className="h-4 w-4 text-[var(--gold)]" />
                  <span className="font-manrope text-sm text-[var(--cream)]" data-testid="vip-active-label">
                    {tierUntil ? t("vip.active_until", { date: fmtDate(tierUntil) }) : t("vip.active_no_date")}
                  </span>
                </div>
                {user && (
                  <button onClick={() => setShowBuy(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="vip-extend-btn">
                    <Coins className="h-4 w-4" /> {t("vip.extend")}
                  </button>
                )}
              </div>
            ) : user ? (
              <button onClick={() => setShowBuy(true)} className="btn-magenta inline-flex items-center gap-2" data-testid="vip-cta">
                <Sparkles className="h-4 w-4" /> {t("vip.cta_buy")}
              </button>
            ) : (
              <Link to="/login" className="btn-magenta inline-flex items-center gap-2"><Sparkles className="h-4 w-4" /> {t("vip.cta_login")}</Link>
            )}
          </div>
        </div>

        {/* PRIS-PAKKER */}
        <div className="mb-12" data-testid="vip-pricing-block">
          <h2 className="font-fraunces italic text-3xl text-[var(--cream)] mb-6">{t("vip.price_title")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {VIP_PACKS.map((p) => (
              <div key={p.key} className={`border p-5 ${p.popular ? "border-[var(--magenta)] ring-1 ring-[var(--magenta)]/30" : "border-white/10"}`}>
                {p.popular && <div className="font-meta text-[var(--magenta)] mb-1">{t("vip.popular")}</div>}
                <div className="font-fraunces italic text-2xl text-[var(--cream)]">{t(p.labelKey)}</div>
                <div className="font-fraunces italic text-3xl text-[var(--gold)] inline-flex items-center gap-2 mt-3">
                  <Coins className="h-5 w-5" /> {p.price}
                </div>
                <div className="font-meta text-[var(--cream-faint)] mt-1">{Math.round(p.price / p.days * 10) / 10} {t("vip.tok_per_day")}</div>
              </div>
            ))}
          </div>
        </div>

        {/* PERKS GRID */}
        <h2 className="font-fraunces italic text-3xl text-[var(--cream)] mb-8">{t("vip.perks_title")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12" data-testid="vip-perks-grid">
          {info.perks.map((p, idx) => {
            const Icon = Icons[p.icon] || Sparkles;
            const titleKey = `vip.perk_${p.icon}_t`;
            const descKey = `vip.perk_${p.icon}_d`;
            // Fallback to backend value if key missing
            const title = t(titleKey, { ...(p.params || {}), defaultValue: p.title });
            const desc  = t(descKey,  { ...(p.params || {}), defaultValue: p.desc  });
            return (
              <div key={idx} className="border border-white/10 p-5 hover:border-[var(--gold)]/40 transition-colors" data-testid={`perk-${idx}`}>
                <Icon className="h-5 w-5 text-[var(--gold)] mb-3" />
                <h3 className="font-fraunces italic text-lg text-[var(--cream)] mb-2">{title}</h3>
                <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed">{desc}</p>
              </div>
            );
          })}
        </div>

      </div>

      <VipBuyModal open={showBuy} onClose={() => setShowBuy(false)} balance={balance}
        onSuccess={() => { refresh && refresh(); api.get("/wallet/me").then(r=>setWallet(r.data)); api.get("/vip/info").then(r=>setInfo(r.data)); }} />
    </Layout>
  );
}
